// Zustand store for the Freehand Draw workspace.
//
// Two kinds of stroke exist:
//   - "draft" strokes are the raw points captured live while drawing.
//   - "panel" strokes are the cleaned, symmetry-replicated closed polygons
//     produced on commit. Only panels are exported.

import { create } from 'zustand';
import { cleanStroke, replicateStroke, strokesToMesh, findSnap, applyM, generateGridPoints, findGridSnap, pickBestSnap, buildShapePolygon, subtractPanel, zoomFromPivot, keepsAxis, SCALE_PIVOTS } from './freehandGeometry';
import { buildPanelSheet } from './panelSheet';
import { buildTemplate } from './polyhedra';
import { smoothPerimeter } from './smooth';
import { projectDecal, densify2d } from './svgImport';const RAINBOW_PALETTE = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#7C3AED', '#EC4899', '#14B8A6', '#EAB308',
];

let panelIdSeq = 1;
const newPanelId = () => `p${panelIdSeq++}`;
let groupIdSeq = 1;
const newGroupId = () => `g${groupIdSeq++}`;
let draftIdSeq = 1;
const newDraftId = () => `d${draftIdSeq++}`;

// Return the panel's on-screen (and exported) perimeter with corner-rounding
// applied. Also re-normalises each point onto the unit sphere so the mesh
// never drifts inside the ball after edits. Points added by smoothing are
// bezier-blended via smoothPerimeter, then reprojected to the sphere.
export function renderedPerimeter(panel) {
  const idxs = panel.points.map((_, i) => i);
  const smoothSet = new Set(panel.smooth || []);
  const raw = smoothPerimeter(idxs, panel.points, smoothSet, 8);
  return raw.map((p) => {
    const l = Math.hypot(p[0], p[1], p[2]) || 1;
    return [p[0] / l, p[1] / l, p[2] / l];
  });
}

function properColoring(faces, palette) {
  // Adjacent test uses shared vertex indices which freehand panels never have.
  // Fall back to alternating palette so neighbours (usually consecutive faces)
  // get different colours.
  return faces.map((_, i) => palette[i % palette.length]);
}

// Subtract each cutter outline from every existing panel it overlaps.
function punchPanels(base, newPanels) {
    // Track every existing-panel group whose contents were touched by the
    // punch. After all cuts run, we also break the *untouched* siblings of
    // those groups out into their own identity groups — otherwise editing
    // one of the leftovers would silently drag the other leftovers with it
    // (via symmetry propagation) even though the group is no longer visually
    // symmetric.
    const dissolvedGroups = new Set();
    for (const cutter of newPanels) {
      base = base.flatMap((existing) => {
        const result = subtractPanel(existing.points, existing.holes || [], cutter.points);
        if (result === null) return [existing];
        if (result.length === 0) {
          if (existing.group) dissolvedGroups.add(existing.group.id);
          return [];
        }
        if (existing.group) dissolvedGroups.add(existing.group.id);
        if (result.length === 1) {
          // A single piece — but its vertex layout has changed from the
          // original (extra intersection points and possibly new holes),
          // so the base-frame index mapping shared with symmetry siblings
          // is no longer valid. Break the panel out into a fresh identity
          // group so vertex edits stay local and don't warp its former
          // siblings.
          const soloGroupId = newGroupId();
          return [{
            ...existing,
            points: result[0].outer,
            holes: result[0].holes || [],
            smooth: [],
            group: {
              id: soloGroupId,
              matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
              matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
              reversed: false,
              symIdx: 0,
            },
          }];
        }
        // Split into multiple pieces — each becomes its own panel with a
        // fresh group so its vertices can be edited independently.
        const cutGroupId = newGroupId();
        return result.map((piece, pi) => ({
          ...existing,
          id: newPanelId(),
          points: piece.outer,
          holes: piece.holes || [],
          smooth: [],
          group: {
            id: cutGroupId,
            matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            reversed: false,
            symIdx: pi,
          },
        }));
      });
    }
    // Second pass: any panel whose original group was dissolved but that
    // itself wasn't cut still needs a fresh group so it can be edited
    // independently of its former siblings.
    if (dissolvedGroups.size) {
      base = base.map((p) => {
        if (!p.group || !dissolvedGroups.has(p.group.id)) return p;
        return {
          ...p,
          group: {
            id: newGroupId(),
            matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            reversed: false,
            symIdx: 0,
          },
        };
      });
    }
  return base;
}

export const useFreehand = create((set, get) => ({
  // ----- tools & options -----
  tool: 'freeform', // 'freeform' | 'polyline' | 'shape' | 'edit' | 'erase'
  rectW: 2,
  rectH: 1,
  svgDraft: null,  // { closed: [{outer, holes}], open: [[u,v]...], pivot, sizeDeg, spinDeg }
  svgGuides: [],   // open SVG lines kept on the ball as tracing guides
  shapeKind: 'hexagon', // 'triangle' | 'square' | 'rectangle' | 'diamond' | 'pentagon' | 'hexagon' | 'circle'
  symmetryMode: 'none',
  ringN: 6,
  symmetryMirror: false, // mirror-coset extension for tetrahedral/octahedral/ring
  snapEnabled: true,
  partitionMode: false, // aggressive edge-snapping while drawing → shared borders
  punchThrough: false,  // new panels subtract themselves from any existing panel they overlap
  spherized: true,       // subdivide panel meshes so they follow the ball surface
  ballOpacity: 1.0,
  referenceImage: null, // { dataUrl, opacity, rotationY }

  // ----- guide lines & grid -----
  showGuides: true,          // pink great-circle guides that follow the pointer
  snapToGrid: false,         // snap points to the nearest grid intersection
  gridType: 'off',           // 'off' | 'latlon' | 'hex' | 'tri'
  gridDensity: 4,            // subdivisions
  gridOpacity: 0.5,          // 0-1 visual opacity of grid markers

  // ----- live drawing state -----
  activeStroke: null, // { points: [[x,y,z], ...] } — being drawn now
  pendingPolyline: null, // { points: [[x,y,z], ...] } — click-by-click WIP
  activeShape: null, // { center: [x,y,z], radius: rad } — being sized now
  hoverPoint: null, // last known raycast hit on the ball (for polyline preview)
  snapTarget: null, // { point, kind, panelId, index } | null

  // ----- committed panels -----
  panels: [], // [{id, points: [[x,y,z]], color}]

  // ----- panel manipulate mode (double-click in edit tool) -----
  manipPanelId: null,     // panel id currently being moved/scaled/rotated
  manipMode: 'move',      // 'move' | 'scale' | 'rotate'

  // Multiplier applied to every edit-mode selection handle so users can
  // shrink the dots when they get in the way of the geometry underneath.
  handleScale: 1.0,       // clamped to [0.25, 2.5] by the setter

  // Cross-workspace visibility toggles. Neither destroys underlying data —
  // both are pure "hide/show" flags on top of state that stays intact.
  referenceHidden: false,   // hide the traced photo without unloading it
  showPanelNumbers: false,  // 3D floating "P{n}" label above each panel

  // ----- history stacks -----
  history: [],
  future: [],

  // ----- getters -----
  // For export & 3D mesh generation we bake corner-rounding into each
  // panel's perimeter so SVG / DXF / STL all inherit the smooth shapes.
  getMesh: () => strokesToMesh(get().panels.map((p) => ({
    points: renderedPerimeter(p),
    holes: (p.holes || []).map((h) => h.map((v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    })),
  }))),
  getColors: () => get().panels.map((p) => p.color),

  // ----- actions: tool state -----
  setTool: (t) => set({ tool: t, activeStroke: null, pendingPolyline: null, activeShape: null, hoverPoint: null, snapTarget: null, manipPanelId: null }),
  setShapeKind: (k) => set({ shapeKind: k }),
  setRectSize: (w, h) => set({ rectW: w, rectH: h }),
  setSvgDraft: (d) => set({ svgDraft: d }),
  updateSvgDraft: (patch) => {
    const d = get().svgDraft;
    if (d) set({ svgDraft: { ...d, ...patch } });
  },
  placeSvgDraft: () => {
    const d = get().svgDraft;
    if (!d) return;
    const opts = { center: SCALE_PIVOTS[d.pivot], sizeRad: (d.sizeDeg * Math.PI) / 180, spinRad: (d.spinDeg * Math.PI) / 180 };
    get()._pushHistory();
    const start = get().panels.length;
    const newPanels = d.closed.map((sh, i) => ({
      id: newPanelId(),
      points: projectDecal(sh.outer, opts),
      holes: sh.holes.map((h) => projectDecal(h, opts)),
      color: RAINBOW_PALETTE[(start + i) % RAINBOW_PALETTE.length],
      smooth: [],
      group: { id: newGroupId(), matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1], matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1], reversed: false, symIdx: 0 },
    }));
    set({
      panels: [...(d.punch === false ? get().panels : punchPanels(get().panels, newPanels)), ...newPanels],
      svgGuides: [...get().svgGuides, ...d.open.map((l) => projectDecal(densify2d(l, false), opts))],
      svgDraft: null,
    });
  },
  clearSvgGuides: () => set({ svgGuides: [] }),
  setSymmetryMode: (m) => set({ symmetryMode: m }),
  setRingN: (n) => set({ ringN: n }),
  setSymmetryMirror: (v) => set({ symmetryMirror: !!v }),
  setSnapEnabled: (v) => set({ snapEnabled: v, snapTarget: null }),
  setPartitionMode: (v) => set({ partitionMode: v }),
  setPunchThrough: (v) => set({ punchThrough: !!v }),
  setSpherized: (v) => set({ spherized: v }),
  setBallOpacity: (v) => set({ ballOpacity: Math.max(0.05, Math.min(1, v)) }),
  setShowGuides: (v) => set({ showGuides: !!v }),
  setSnapToGrid: (v) => set({ snapToGrid: !!v }),
  setGridType: (t) => set({ gridType: t, snapToGrid: t !== 'off' ? get().snapToGrid : false }),
  setGridDensity: (n) => set({ gridDensity: Math.max(1, Math.min(12, Math.round(n))) }),
  setGridOpacity: (v) => set({ gridOpacity: Math.max(0, Math.min(1, v)) }),
  setHandleScale: (v) => set({ handleScale: Math.max(0.25, Math.min(2.5, v)) }),
  setReferenceHidden: (v) => set({ referenceHidden: !!v }),
  setShowPanelNumbers: (v) => set({ showPanelNumbers: !!v }),

  // Compute the currently-active grid points (memoised in the geometry module).
  _gridPoints: () => {
    const { snapToGrid, gridType, gridDensity } = get();
    if (!snapToGrid || gridType === 'off') return null;
    return generateGridPoints(gridType, gridDensity);
  },
  // Unified snap resolver — panels + grid, priority vertex > edge > grid.
  _resolveSnap: (p, thresholdRad = (5 * Math.PI) / 180, exclude = null) => {
    const { snapEnabled, panels } = get();
    const panelSnap = snapEnabled ? findSnap(p, panels, thresholdRad, exclude) : null;
    const grid = get()._gridPoints();
    const gridSnap = grid ? findGridSnap(p, grid, thresholdRad) : null;
    return pickBestSnap(panelSnap, gridSnap);
  },
  setReferenceImage: (dataUrl) => set({
    referenceImage: dataUrl ? { dataUrl, opacity: 1, rotationY: 0, scaleX: 1, scaleY: 1 } : null,
    // Auto-fade the ball so the reference is actually visible.
    ballOpacity: dataUrl ? 0.25 : get().ballOpacity,
  }),
  setReferenceRotationY: (r) => {
    const ref = get().referenceImage;
    if (ref) set({ referenceImage: { ...ref, rotationY: r } });
  },
  setReferenceScaleX: (v) => {
    const ref = get().referenceImage;
    if (ref) set({ referenceImage: { ...ref, scaleX: Math.max(0.25, Math.min(4, v)) } });
  },
  setReferenceScaleY: (v) => {
    const ref = get().referenceImage;
    if (ref) set({ referenceImage: { ...ref, scaleY: Math.max(0.25, Math.min(4, v)) } });
  },
  clearReferenceImage: () => set({ referenceImage: null, ballOpacity: 1 }),

  // Called on every pointer move over the ball while a drawing tool is
  // active. Updates the current hover position and the snap indicator so the
  // viewport can render a "snap-to-corner/edge" bullet.
  updateHover: (p) => {
    if (!p) { set({ hoverPoint: null, snapTarget: null }); return; }
    const target = get()._resolveSnap(p);
    set({ hoverPoint: p, snapTarget: target });
  },
  clearHover: () => set({ hoverPoint: null, snapTarget: null }),

  // ----- freeform drawing -----
  beginFreeform: (p) => {
    const snap = get()._resolveSnap(p);
    set({ activeStroke: { points: [snap ? snap.point : p] } });
  },
  extendFreeform: (p) => {
    const s = get().activeStroke;
    if (!s) return;
    const last = s.points[s.points.length - 1];
    // Skip points closer than ~0.5° to reduce noise.
    const dot = Math.max(-1, Math.min(1, last[0] * p[0] + last[1] * p[1] + last[2] * p[2]));
    if (Math.acos(dot) < 0.00873) return;
    // In Partition Mode every intermediate point also snaps to the nearest
    // existing edge so the resulting panel shares its border with neighbours.
    const { partitionMode } = get();
    let pt = p;
    if (partitionMode) {
      const snap = get()._resolveSnap(p, (5 * Math.PI) / 180);
      if (snap) pt = snap.point;
    }
    set({ activeStroke: { points: [...s.points, pt] } });
  },
  finishFreeform: () => {
    const s = get().activeStroke;
    set({ activeStroke: null });
    if (!s || s.points.length < 6) return;
    // Snap the closing point too, so the final loop can share a corner with
    // an existing panel or grid intersection.
    const last = s.points[s.points.length - 1];
    const snap = get()._resolveSnap(last);
    const pts = snap ? [...s.points.slice(0, -1), snap.point] : s.points;
    get()._commitDraft(pts, { smooth: true });
  },
  cancelFreeform: () => set({ activeStroke: null }),

  // ----- polyline drawing -----
  polylineAddPoint: (p) => {
    const snap = get()._resolveSnap(p);
    const point = snap ? snap.point : p;
    const s = get().pendingPolyline;
    const points = s ? [...s.points, point] : [point];
    set({ pendingPolyline: { points } });
  },
  polylineUndoPoint: () => {
    const s = get().pendingPolyline;
    if (!s) return;
    const next = s.points.slice(0, -1);
    if (!next.length) set({ pendingPolyline: null });
    else set({ pendingPolyline: { points: next } });
  },
  polylineCommit: () => {
    const s = get().pendingPolyline;
    set({ pendingPolyline: null });
    if (!s || s.points.length < 3) return;
    // Polylines keep their straight edges — no Chaikin smoothing, no
    // resampling — so the panel matches exactly what the user clicked.
    get()._commitDraft(s.points, { smooth: false });
  },
  polylineCancel: () => set({ pendingPolyline: null }),

  // ----- shape stamp (starter regular polygons) -----
  beginShape: (p) => {
    const snap = get()._resolveSnap(p);
    set({ activeShape: { center: snap ? snap.point : p, radius: 0 } });
  },
  updateShape: (p) => {
    const s = get().activeShape;
    if (!s) return;
    const c = s.center;
    const dot = Math.max(-1, Math.min(1, c[0] * p[0] + c[1] * p[1] + c[2] * p[2]));
    const r = Math.acos(dot);
    set({ activeShape: { center: c, radius: r } });
  },
  finishShape: () => {
    const s = get().activeShape;
    set({ activeShape: null });
    if (!s || s.radius < 0.02) return; // <~1.15° — treat as accidental click
    const { shapeKind } = get();
    const pts = buildShapePolygon(s.center, s.radius, shapeKind, get().rectW / get().rectH);
    if (!pts || pts.length < 3) return;
    get()._commitDraft(pts, { smooth: false });
  },
  cancelShape: () => set({ activeShape: null }),

  // ----- panel management -----
  _commitDraft: (rawPoints, { smooth = true } = {}) => {
    const cleaned = cleanStroke(rawPoints, smooth ? {} : {
      // Polyline: keep sharp corners, just dedupe and gate for degenerate loops.
      smoothIterations: 0,
      targetSpacingDeg: null,
    });
    if (!cleaned) return;
    const { symmetryMode, ringN, symmetryMirror, panels, punchThrough } = get();
    const replicas = replicateStroke(cleaned, symmetryMode, ringN, symmetryMirror);
    const palette = RAINBOW_PALETTE;
    const startIdx = panels.length;
    // Panels created together share a groupId so edits propagate across them.
    const groupId = newGroupId();
    const newPanels = replicas.map((r, i) => ({
      id: newPanelId(),
      points: r.points,
      holes: [],
      color: palette[(startIdx + i) % palette.length],
      smooth: [], // vertex indices flagged as smooth for this panel
      group: {
        id: groupId,
        matrix: r.matrix,
        matrixInv: r.matrixInv,
        reversed: !!r.reversed,
        symIdx: r.symIdx,
      },
    }));
    get()._pushHistory();

    // If Punch-through mode is on, subtract every new replica from any
    // pre-existing panel it overlaps. Each replica processes the panels
    // stack in turn, so cuts compound if multiple replicas hit the same
    // panel. A cut that splits a panel yields multiple pieces with fresh
    // panel ids and a fresh group (symmetry propagation is preserved across
    // the ball because every symmetry sibling of the cutter also runs its
    // own cut on its counterpart panel).
    let base = panels;
    if (punchThrough) base = punchPanels(base, newPanels);

    set({ panels: [...base, ...newPanels] });
  },
  deletePanel: (id) => {
    get()._pushHistory();
    set({ panels: get().panels.filter((p) => p.id !== id) });
  },

  // Move a single vertex of a panel. If the panel belongs to a symmetry
  // group, the same edit propagates to every sibling panel via each panel's
  // own transform so the ball's symmetry stays intact.
  beginEdit: () => { get()._pushHistory(); },
  moveVertex: (panelId, index, newPoint) => {
    const panels = get().panels;
    const src = panels.find((p) => p.id === panelId);
    if (!src) return;
    const n = src.points.length;
    // Convert the new position into "base frame" (i.e. through src's inverse).
    const baseIdx = src.group.reversed ? n - 1 - index : index;
    let basePos = applyM(src.group.matrixInv, newPoint);
    const bl = Math.hypot(basePos[0], basePos[1], basePos[2]) || 1;
    basePos = [basePos[0] / bl, basePos[1] / bl, basePos[2] / bl];
    set({
      panels: panels.map((p) => {
        if (!p.group || p.group.id !== src.group.id) return p;
        // Compute this sibling's index for the same base vertex.
        const sibN = p.points.length;
        const sibI = p.group.reversed ? sibN - 1 - baseIdx : baseIdx;
        if (sibI < 0 || sibI >= sibN) return p;
        let sibPos = applyM(p.group.matrix, basePos);
        const sl = Math.hypot(sibPos[0], sibPos[1], sibPos[2]) || 1;
        sibPos = [sibPos[0] / sl, sibPos[1] / sl, sibPos[2] / sl];
        const points = p.points.map((v, i) => (i === sibI ? sibPos : v));
        return { ...p, points };
      }),
    });
  },
  toggleSmoothVertex: (panelId, index) => {
    const panels = get().panels;
    const src = panels.find((p) => p.id === panelId);
    if (!src) return;
    const n = src.points.length;
    const baseIdx = src.group.reversed ? n - 1 - index : index;
    const willSmooth = !(src.smooth || []).includes(index);
    get()._pushHistory();
    set({
      panels: panels.map((p) => {
        if (!p.group || p.group.id !== src.group.id) return p;
        const sibN = p.points.length;
        const sibI = p.group.reversed ? sibN - 1 - baseIdx : baseIdx;
        if (sibI < 0 || sibI >= sibN) return p;
        const cur = new Set(p.smooth || []);
        if (willSmooth) cur.add(sibI);
        else cur.delete(sibI);
        return { ...p, smooth: Array.from(cur).sort((a, b) => a - b) };
      }),
    });
  },
  removeVertex: (panelId, index) => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src || src.points.length <= 3) return;
    const n = src.points.length;
    const baseIdx = src.group.reversed ? n - 1 - index : index;
    get()._pushHistory();
    set({
      panels: get().panels.map((p) => {
        if (!p.group || p.group.id !== src.group.id) return p;
        const sibN = p.points.length;
        if (sibN <= 3) return p;
        const sibI = p.group.reversed ? sibN - 1 - baseIdx : baseIdx;
        if (sibI < 0 || sibI >= sibN) return p;
        const points = p.points.filter((_, i) => i !== sibI);
        const smooth = (p.smooth || [])
          .filter((s) => s !== sibI)
          .map((s) => (s > sibI ? s - 1 : s));
        return { ...p, points, smooth };
      }),
    });
  },

  // Insert a new corner along an existing edge (between vertex `edgeIdx` and
  // vertex `edgeIdx + 1`). If `position` is null, the midpoint is used. The
  // new vertex slots into position `edgeIdx + 1`, shifting subsequent
  // indices +1 (including any smooth flags). Propagates through the symmetry
  // group: every sibling gets a corresponding insertion.
  insertVertexOnEdge: (panelId, edgeIdx, position = null) => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src) return;
    const nSrc = src.points.length;
    if (edgeIdx < 0 || edgeIdx >= nSrc) return;
    const a = src.points[edgeIdx];
    const b = src.points[(edgeIdx + 1) % nSrc];
    const pt = position || [
      (a[0] + b[0]) / 2,
      (a[1] + b[1]) / 2,
      (a[2] + b[2]) / 2,
    ];
    // Re-project to the unit sphere.
    const l = Math.hypot(pt[0], pt[1], pt[2]) || 1;
    const srcInsertPt = [pt[0] / l, pt[1] / l, pt[2] / l];
    // Base-frame position via source panel's inverse transform.
    let base = applyM(src.group.matrixInv, srcInsertPt);
    const bl = Math.hypot(base[0], base[1], base[2]) || 1;
    base = [base[0] / bl, base[1] / bl, base[2] / bl];
    // Base edge index — in normal panels: edgeIdx. In reversed panels the
    // stored points are reversed, so edge `edgeIdx` corresponds to base edge
    // `nSrc - 2 - edgeIdx` (the edge between base vertices n-2-edgeIdx and
    // n-1-edgeIdx). See notes in moveVertex for the reversal mapping.
    const baseEdgeIdx = src.group.reversed ? nSrc - 2 - edgeIdx : edgeIdx;
    get()._pushHistory();
    set({
      panels: get().panels.map((p) => {
        if (!p.group || p.group.id !== src.group.id) return p;
        const n = p.points.length;
        // Sibling edge index maps from base edge.
        const sibEdge = p.group.reversed ? n - 2 - baseEdgeIdx : baseEdgeIdx;
        // Insertion slot is edge+1 in normal (right after left endpoint).
        // For reversed panels we still insert AFTER the local left endpoint,
        // which is `sibEdge`, so slot = sibEdge + 1.
        const slot = sibEdge + 1;
        // Compute sibling position by forward-applying its transform to base.
        let sibPos = applyM(p.group.matrix, base);
        const sl = Math.hypot(sibPos[0], sibPos[1], sibPos[2]) || 1;
        sibPos = [sibPos[0] / sl, sibPos[1] / sl, sibPos[2] / sl];
        const points = [...p.points.slice(0, slot), sibPos, ...p.points.slice(slot)];
        // Smooth flags on vertices at index >= slot shift by +1.
        const smooth = (p.smooth || []).map((s) => (s >= slot ? s + 1 : s));
        return { ...p, points, smooth };
      }),
    });
  },
  // ----- hole editing (punched-through panels) -----
  // Holes are stored per-panel in world coordinates (no group propagation)
  // because they arise from boolean subtraction rather than a shared base
  // stroke. Symmetry is preserved at cut-time — every sibling of the cutter
  // performs its own subtraction on its counterpart panel — but subsequent
  // edits to a hole only affect that panel.
  moveHoleVertex: (panelId, holeIdx, vertexIdx, newPoint) => {
    const l = Math.hypot(newPoint[0], newPoint[1], newPoint[2]) || 1;
    const p = [newPoint[0] / l, newPoint[1] / l, newPoint[2] / l];
    set({
      panels: get().panels.map((panel) => {
        if (panel.id !== panelId) return panel;
        const holes = (panel.holes || []).map((h, hi) => {
          if (hi !== holeIdx) return h;
          return h.map((v, vi) => (vi === vertexIdx ? p : v));
        });
        return { ...panel, holes };
      }),
    });
  },
  insertHoleVertexOnEdge: (panelId, holeIdx, edgeIdx) => {
    get()._pushHistory();
    set({
      panels: get().panels.map((panel) => {
        if (panel.id !== panelId) return panel;
        const holes = (panel.holes || []).map((h, hi) => {
          if (hi !== holeIdx) return h;
          const n = h.length;
          const a = h[edgeIdx];
          const b = h[(edgeIdx + 1) % n];
          // Great-circle midpoint (slerp with t=0.5).
          const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
          const omega = Math.acos(dot);
          let mid;
          if (omega < 1e-6) {
            mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
          } else {
            const s = Math.sin(omega);
            const c1 = Math.sin(omega * 0.5) / s;
            mid = [a[0] * c1 + b[0] * c1, a[1] * c1 + b[1] * c1, a[2] * c1 + b[2] * c1];
          }
          const ml = Math.hypot(mid[0], mid[1], mid[2]) || 1;
          mid = [mid[0] / ml, mid[1] / ml, mid[2] / ml];
          return [...h.slice(0, edgeIdx + 1), mid, ...h.slice(edgeIdx + 1)];
        });
        return { ...panel, holes };
      }),
    });
  },
  deleteHoleVertex: (panelId, holeIdx, vertexIdx) => {
    get()._pushHistory();
    set({
      panels: get().panels.map((panel) => {
        if (panel.id !== panelId) return panel;
        const holes = (panel.holes || [])
          .map((h, hi) => {
            if (hi !== holeIdx) return h;
            if (h.length <= 3) return null; // drop hole if it would become degenerate
            return h.filter((_, vi) => vi !== vertexIdx);
          })
          .filter(Boolean);
        return { ...panel, holes };
      }),
    });
  },

  // ----- panel manipulate (move/scale/rotate whole panel + siblings) -----
  setManipPanel: (id) => set({ manipPanelId: id, manipMode: 'move' }),
  setManipMode: (m) => set({ manipMode: m }),
  exitManip: () => set({ manipPanelId: null }),

  // Apply a per-vertex world-space transform `fn(p) → newP` to a panel and
  // every sibling in its symmetry group. Rebuilds each sibling's points from
  // the source's new base-frame positions so the group stays perfectly
  // interlocked no matter which sibling was double-clicked.
  _applyPanelTransform: (panelId, fn) => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src || !src.group) return;
    const n = src.points.length;
    const nrm = (v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    };
    // 1. Transform source panel's points.
    const newSrcPts = src.points.map((p) => nrm(fn(p)));
    // 2. Convert into base-frame indexing.
    const basePts = new Array(n);
    for (let i = 0; i < n; i++) {
      const srcIdx = src.group.reversed ? n - 1 - i : i;
      basePts[i] = nrm(applyM(src.group.matrixInv, newSrcPts[srcIdx]));
    }
    // 3. Rebuild every sibling from those base positions.
    set({
      panels: get().panels.map((p) => {
        if (!p.group || p.group.id !== src.group.id) return p;
        const nSib = p.points.length;
        if (nSib !== n) return p;
        const pts = new Array(nSib);
        for (let i = 0; i < nSib; i++) {
          const baseIdx = p.group.reversed ? nSib - 1 - i : i;
          pts[i] = nrm(applyM(p.group.matrix, basePts[baseIdx]));
        }
        // Holes: since punched panels use identity-matrix groups (no
        // cross-sibling coupling), just apply the world-space transform
        // directly to each hole vertex of THIS panel only.
        let newHoles = p.holes;
        if (p.id === panelId && p.holes && p.holes.length) {
          newHoles = p.holes.map((h) => h.map((v) => nrm(fn(v))));
        }
        return { ...p, points: pts, holes: newHoles };
      }),
    });
  },

  movePanel: (panelId, fromDir, toDir) => {
    const nrm = (v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    };
    const f = nrm(fromDir), t = nrm(toDir);
    const dp = Math.max(-1, Math.min(1, f[0] * t[0] + f[1] * t[1] + f[2] * t[2]));
    if (dp > 0.99999) return;
    let ax = [f[1] * t[2] - f[2] * t[1], f[2] * t[0] - f[0] * t[2], f[0] * t[1] - f[1] * t[0]];
    const al = Math.hypot(ax[0], ax[1], ax[2]);
    if (al < 1e-9) return;
    ax = [ax[0] / al, ax[1] / al, ax[2] / al];
    const ang = Math.acos(dp);
    const c = Math.cos(ang), s = Math.sin(ang), oneMC = 1 - c;
    const rot = (p) => {
      const [x, y, z] = p, [kx, ky, kz] = ax;
      const d = kx * x + ky * y + kz * z;
      return [
        x * c + (ky * z - kz * y) * s + kx * d * oneMC,
        y * c + (kz * x - kx * z) * s + ky * d * oneMC,
        z * c + (kx * y - ky * x) * s + kz * d * oneMC,
      ];
    };
    get()._applyPanelTransform(panelId, rot);
  },

  scalePanel: (panelId, factor) => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src) return;
    const cSum = [0, 0, 0];
    for (const p of src.points) { cSum[0] += p[0]; cSum[1] += p[1]; cSum[2] += p[2]; }
    const cl = Math.hypot(cSum[0], cSum[1], cSum[2]) || 1;
    const cn = [cSum[0] / cl, cSum[1] / cl, cSum[2] / cl];
    const scaleFn = (v) => {
      const dp = Math.max(-1, Math.min(1, v[0] * cn[0] + v[1] * cn[1] + v[2] * cn[2]));
      const ang = Math.acos(dp);
      if (ang < 1e-6) return v;
      const tx = v[0] - dp * cn[0], ty = v[1] - dp * cn[1], tz = v[2] - dp * cn[2];
      const tl = Math.hypot(tx, ty, tz) || 1;
      const dir = [tx / tl, ty / tl, tz / tl];
      const newAng = Math.max(0.005, Math.min(Math.PI * 0.75, ang * factor));
      const cA = Math.cos(newAng), sA = Math.sin(newAng);
      return [cA * cn[0] + sA * dir[0], cA * cn[1] + sA * dir[1], cA * cn[2] + sA * dir[2]];
    };
    get()._applyPanelTransform(panelId, scaleFn);
  },

  rotatePanel: (panelId, angleRad) => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src) return;
    const cSum = [0, 0, 0];
    for (const p of src.points) { cSum[0] += p[0]; cSum[1] += p[1]; cSum[2] += p[2]; }
    const cl = Math.hypot(cSum[0], cSum[1], cSum[2]) || 1;
    const cn = [cSum[0] / cl, cSum[1] / cl, cSum[2] / cl];
    const c = Math.cos(angleRad), s = Math.sin(angleRad), oneMC = 1 - c;
    const rot = (v) => {
      const [x, y, z] = v, [kx, ky, kz] = cn;
      const d = kx * x + ky * y + kz * z;
      return [
        x * c + (ky * z - kz * y) * s + kx * d * oneMC,
        y * c + (kz * x - kx * z) * s + ky * d * oneMC,
        z * c + (kx * y - ky * x) * s + kz * d * oneMC,
      ];
    };
    get()._applyPanelTransform(panelId, rot);
  },

  // Duplicate a panel to a new independent copy. `mode` controls how the
  // copy is placed:
  //   'copy'       — rotate 30° yaw so it lands to one side of the source
  //   'antipodal'  — through-centre inversion (opposite side of the ball)
  //   'mirrorX/Y/Z' — reflect across the YZ / XZ / XY plane
  duplicatePanel: (panelId, mode = 'copy') => {
    const src = get().panels.find((p) => p.id === panelId);
    if (!src) return;
    const nrm = (v) => {
      const l = Math.hypot(v[0], v[1], v[2]) || 1;
      return [v[0] / l, v[1] / l, v[2] / l];
    };
    let transform;
    switch (mode) {
      case 'antipodal':
        transform = (p) => [-p[0], -p[1], -p[2]];
        break;
      case 'mirrorX':
        transform = (p) => [-p[0], p[1], p[2]];
        break;
      case 'mirrorY':
        transform = (p) => [p[0], -p[1], p[2]];
        break;
      case 'mirrorZ':
        transform = (p) => [p[0], p[1], -p[2]];
        break;
      case 'copy':
      default: {
        // 30° yaw around the world Y axis so the copy sits next to the source
        // on the same latitude — ready for the manipulate tool to fine-tune.
        const c = Math.cos(Math.PI / 6);
        const s = Math.sin(Math.PI / 6);
        transform = (p) => [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c];
        break;
      }
    }
    const newPoints = src.points.map((p) => nrm(transform(p)));
    const newHoles = (src.holes || []).map((h) => h.map((p) => nrm(transform(p))));
    get()._pushHistory();
    set({
      panels: [
        ...get().panels,
        {
          id: newPanelId(),
          points: newPoints,
          holes: newHoles,
          color: src.color,
          smooth: [],
          group: {
            id: newGroupId(),
            matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
            reversed: false,
            symIdx: 0,
          },
        },
      ],
    });
  },

  setPanelColor: (id, color) => {
    set({
      panels: get().panels.map((p) => (p.id === id ? { ...p, color } : p)),
    });
  },
  setAllColors: (colorA, colorB) => {
    set({
      panels: get().panels.map((p, i) => ({
        ...p,
        color: i % 2 === 0 ? colorA : colorB,
      })),
    });
  },
  setRainbow: () => {
    set({
      panels: get().panels.map((p, i) => ({
        ...p,
        color: RAINBOW_PALETTE[i % RAINBOW_PALETTE.length],
      })),
    });
  },
  clearAll: () => {
    get()._pushHistory();
    set({ panels: [], activeStroke: null, pendingPolyline: null });
  },

  // ----- rotate every panel around the ball origin -----
  // Useful when a whole composition ends up off-axis and the user wants to
  // realign it to the ball's XYZ. We rotate every panel's vertices AND the
  // symmetry group matrices consistently — for a group matrix M mapping
  // base→panel, the new matrix under a world rotation R is M' = R·M·Rᵀ,
  // matrixInv' = R·Minv·Rᵀ. That keeps future vertex edits landing in the
  // right place through moveVertex/applyM.
  rotateAll: (axis, degrees) => {
    if (!degrees) return;
    const rad = (degrees * Math.PI) / 180;
    const c = Math.cos(rad), s = Math.sin(rad);
    // Row-major 3x3 rotation about the chosen axis.
    let R;
    if (axis === 'x') R = [1, 0, 0,  0, c, -s,  0, s, c];
    else if (axis === 'y') R = [c, 0, s,  0, 1, 0,  -s, 0, c];
    else R = [c, -s, 0,  s, c, 0,  0, 0, 1];
    // Transpose = inverse (rotations are orthogonal).
    const Rt = [R[0], R[3], R[6], R[1], R[4], R[7], R[2], R[5], R[8]];
    const mul = (A, B) => [
      A[0]*B[0]+A[1]*B[3]+A[2]*B[6], A[0]*B[1]+A[1]*B[4]+A[2]*B[7], A[0]*B[2]+A[1]*B[5]+A[2]*B[8],
      A[3]*B[0]+A[4]*B[3]+A[5]*B[6], A[3]*B[1]+A[4]*B[4]+A[5]*B[7], A[3]*B[2]+A[4]*B[5]+A[5]*B[8],
      A[6]*B[0]+A[7]*B[3]+A[8]*B[6], A[6]*B[1]+A[7]*B[4]+A[8]*B[7], A[6]*B[2]+A[7]*B[5]+A[8]*B[8],
    ];
    const rot = (p) => {
      const x = R[0]*p[0] + R[1]*p[1] + R[2]*p[2];
      const y = R[3]*p[0] + R[4]*p[1] + R[5]*p[2];
      const z = R[6]*p[0] + R[7]*p[1] + R[8]*p[2];
      const l = Math.hypot(x, y, z) || 1;
      return [x / l, y / l, z / l];
    };
    get()._pushHistory();
    set({
      panels: get().panels.map((p) => ({
        ...p,
        points: p.points.map(rot),
        holes: (p.holes || []).map((ring) => ring.map(rot)),
        group: p.group ? {
          ...p.group,
          matrix: mul(mul(R, p.group.matrix), Rt),
          matrixInv: mul(mul(R, p.group.matrixInv), Rt),
        } : p.group,
      })),
      svgGuides: get().svgGuides.map((g) => g.map(rot)),
      activeStroke: null,
      pendingPolyline: null,
      manipPanelId: null,
    });
  },

  // ----- scale the whole layout about a chosen side of the ball -----
  // Groups whose matrices don't fix the pivot can't stay linked after the
  // zoom, so their panels are split into independent identity groups.
  _scaledPanels: (src, k) => {
    const c = SCALE_PIVOTS[get().scalePivot];
    const brokenGroups = new Set();
    src.forEach((p) => {
      if (p.group && (!keepsAxis(p.group.matrix, c) || !keepsAxis(p.group.matrixInv, c))) brokenGroups.add(p.group.id);
    });
    const z = (v) => zoomFromPivot(v, k, c);
    return src.map((p) => ({
      ...p,
      points: p.points.map(z),
      holes: (p.holes || []).map((ring) => ring.map(z)),
      group: p.group && brokenGroups.has(p.group.id)
        ? { id: newGroupId(), matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1], matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1], reversed: false, symIdx: 0 }
        : p.group,
    }));
  },
  scaleAllSnapshot: null,
  scalePivot: 'top',
  scaleMarkerVisible: false,
  setScalePivot: (id) => { get().commitScaleAll(); set({ scalePivot: id }); },
  setScaleMarkerVisible: (v) => set({ scaleMarkerVisible: v }),
  previewScaleAll: (k) => {
    let snap = get().scaleAllSnapshot;
    if (!snap) {
      if (!get().panels.length) return;
      get()._pushHistory();
      snap = get().panels;
      set({ scaleAllSnapshot: snap, scaleGuidesSnapshot: get().svgGuides, activeStroke: null, pendingPolyline: null, manipPanelId: null });
    }
    set({
      panels: k === 1 ? snap : get()._scaledPanels(snap, k),
      svgGuides: get()._scaledGuides(get().scaleGuidesSnapshot, k),
    });
  },
  _scaledGuides: (guides, k) => {
    const c = SCALE_PIVOTS[get().scalePivot];
    return k === 1 ? guides : guides.map((g) => g.map((v) => zoomFromPivot(v, k, c)));
  },
  commitScaleAll: () => set({ scaleAllSnapshot: null, scaleGuidesSnapshot: null }),
  cancelScaleAll: () => {
    const snap = get().scaleAllSnapshot;
    if (snap) set({ panels: snap, svgGuides: get().scaleGuidesSnapshot || get().svgGuides, scaleAllSnapshot: null, scaleGuidesSnapshot: null, history: get().history.slice(0, -1) });
  },
  scaleAll: (k) => {
    if (!get().panels.length || k === 1) return;
    get()._pushHistory();
    set({
      panels: get()._scaledPanels(get().panels, k),
      svgGuides: get()._scaledGuides(get().svgGuides, k),
      scaleAllSnapshot: null,
      activeStroke: null,
      pendingPolyline: null,
      manipPanelId: null,
    });
  },

  // ----- fill ball with a preset polyhedral template -----
  // Reuses the Studio's `buildTemplate` geometry (truncated icosahedron,
  // dodecahedron, etc.) and materialises each face as an independent Freehand
  // panel. Every panel gets its own group id so editing one never drags
  // another; the user can then reshape, punch, delete or duplicate at will.
  // Replaces existing panels — callers are expected to `confirm()` first if
  // that matters.
  fillTemplate: (templateId) => {
    const tpl = buildTemplate(templateId);
    if (!tpl || !tpl.vertices || !tpl.faces) return;
    const palette = RAINBOW_PALETTE;
    const newPanels = tpl.faces.map((face, i) => {
      const pts = face.map((vi) => {
        const v = tpl.vertices[vi];
        const l = Math.hypot(v[0], v[1], v[2]) || 1;
        return [v[0] / l, v[1] / l, v[2] / l];
      });
      return {
        id: newPanelId(),
        points: pts,
        holes: [],
        color: palette[i % palette.length],
        smooth: [],
        group: {
          id: newGroupId(),
          matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
          matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
          reversed: false,
          symIdx: 0,
        },
      };
    });
    get()._pushHistory();
    set({ panels: newPanels, activeStroke: null, pendingPolyline: null, manipPanelId: null });
  },

  // ----- hand-off from the geometric Studio workspace -----
  // Takes the Studio's live {vertices, faces, colors, smoothVertices} and
  // materialises every face as its own Freehand panel. Each panel gets its
  // own group id so later edits stay local, and we project vertices back
  // onto the unit sphere so the mesh never drifts after dragging. Smooth-
  // corner flags are translated from vertex-indexed → per-panel indexed so
  // the rounded corners survive the hand-off.
  loadFromStudio: ({ vertices, faces, colors, smoothVertices } = {}) => {
    if (!Array.isArray(vertices) || !Array.isArray(faces) || !faces.length) return;
    const smoothSet = new Set(smoothVertices || []);
    const palette = RAINBOW_PALETTE;
    const newPanels = faces.map((face, i) => {
      const pts = face.map((vi) => {
        const v = vertices[vi];
        const l = Math.hypot(v[0], v[1], v[2]) || 1;
        return [v[0] / l, v[1] / l, v[2] / l];
      });
      // Translate smooth vertex-indices → positions along this face's own
      // point array.
      const smooth = [];
      face.forEach((vi, j) => { if (smoothSet.has(vi)) smooth.push(j); });
      return {
        id: newPanelId(),
        points: pts,
        holes: [],
        color: (colors && colors[i]) || palette[i % palette.length],
        smooth,
        group: {
          id: newGroupId(),
          matrix: [1, 0, 0, 0, 1, 0, 0, 0, 1],
          matrixInv: [1, 0, 0, 0, 1, 0, 0, 0, 1],
          reversed: false,
          symIdx: 0,
        },
      };
    });
    get()._pushHistory();
    set({
      panels: newPanels,
      activeStroke: null,
      pendingPolyline: null,
      manipPanelId: null,
    });
  },

  // ----- gallery: load a saved freehand design back into the store -----
  loadDesign: (design) => {
    if (!design) return;
    // Preferred: the saved doc includes the raw freehand_panels array with
    // per-panel group transforms so edits still propagate. Fall back to
    // deriving trivial panels from vertices/faces if a legacy design is
    // loaded (no group data — those panels won't have symmetry siblings).
    const raw = design.freehand_panels;
    let newPanels;
    if (Array.isArray(raw) && raw.length) {
      const groupId = `g${groupIdSeq++}`;
      newPanels = raw.map((p, i) => ({
        id: newPanelId(),
        points: p.points.map((v) => [v[0], v[1], v[2]]),
        color: p.color || RAINBOW_PALETTE[i % RAINBOW_PALETTE.length],
        smooth: (p.smooth || []).slice(),
        group: p.group
          ? { ...p.group }
          : { id: groupId, matrix: [1,0,0,0,1,0,0,0,1], matrixInv: [1,0,0,0,1,0,0,0,1], reversed: false, symIdx: i },
      }));
    } else if (design.faces && design.vertices) {
      const groupId = `g${groupIdSeq++}`;
      newPanels = design.faces.map((face, i) => ({
        id: newPanelId(),
        points: face.map((vi) => design.vertices[vi]),
        color: (design.colors && design.colors[i]) || RAINBOW_PALETTE[i % RAINBOW_PALETTE.length],
        smooth: [],
        group: { id: groupId, matrix: [1,0,0,0,1,0,0,0,1], matrixInv: [1,0,0,0,1,0,0,0,1], reversed: false, symIdx: i },
      }));
    } else {
      newPanels = [];
    }
    // Restore the traced reference photo if one was saved with the design.
    // Falls back to no-op for legacy designs that predate this feature.
    const savedRef = design.reference_image;
    const nextReferenceImage = savedRef && savedRef.dataUrl
      ? {
          dataUrl: savedRef.dataUrl,
          opacity: 1,
          rotationY: savedRef.rotationY || 0,
          scaleX: savedRef.scaleX ?? 1,
          scaleY: savedRef.scaleY ?? 1,
        }
      : null;
    set({
      panels: newPanels,
      history: [],
      future: [],
      activeStroke: null,
      pendingPolyline: null,
      referenceImage: nextReferenceImage,
      // Auto-fade the ball if we restored a reference image so it's visible.
      ballOpacity: nextReferenceImage ? 0.25 : get().ballOpacity,
    });
    // Advance the global id counters past any ids present in the loaded
    // design so newly-drawn panels can never collide with a pre-existing
    // panel or group. Without this, a fresh page load resets the counters
    // to 1 while loaded panels still reference `g5`/`p3` etc., and the next
    // draw hands out `g1`/`p1` — the collision made vertex edits propagate
    // between unrelated panels sharing the recycled group id.
    for (const p of newPanels) {
      const pn = parseInt(String(p.id).slice(1), 10);
      if (Number.isFinite(pn) && pn >= panelIdSeq) panelIdSeq = pn + 1;
      const gn = parseInt(String(p.group && p.group.id).slice(1), 10);
      if (Number.isFinite(gn) && gn >= groupIdSeq) groupIdSeq = gn + 1;
    }
  },

  // ----- history -----
  _pushHistory: () => {
    const { history, panels } = get();
    const snap = panels.map((p) => ({
      ...p,
      points: p.points.map((v) => v.slice()),
      smooth: (p.smooth || []).slice(),
    }));
    set({ history: [...history, snap].slice(-50), future: [] });
  },
  undo: () => {
    const { history, future, panels } = get();
    if (!history.length) return;
    const prev = history[history.length - 1];
    const snap = panels.map((p) => ({
      ...p,
      points: p.points.map((v) => v.slice()),
      smooth: (p.smooth || []).slice(),
    }));
    set({
      panels: prev,
      history: history.slice(0, -1),
      future: [...future, snap],
      activeStroke: null,
      pendingPolyline: null,
      scaleAllSnapshot: null,
    });
  },
  redo: () => {
    const { history, future, panels } = get();
    if (!future.length) return;
    const next = future[future.length - 1];
    const snap = panels.map((p) => ({
      ...p,
      points: p.points.map((v) => v.slice()),
      smooth: (p.smooth || []).slice(),
    }));
    set({
      panels: next,
      future: future.slice(0, -1),
      history: [...history, snap],
      activeStroke: null,
      pendingPolyline: null,
      scaleAllSnapshot: null,
    });
  },
}));

export const FREEHAND_PALETTE = RAINBOW_PALETTE;

if (typeof window !== 'undefined') {
  window.__freehand = {
    get: () => useFreehand.getState(),
    addPanel: (points) => useFreehand.getState()._commitDraft(points),
    buildSheet: (opts = {}) => {
      const m = useFreehand.getState().getMesh();
      return buildPanelSheet(m.vertices, m.faces, {
        ballDiameterMM: 213.2,
        seamAllowanceMM: 2.5,
        holeDiameterMM: 1,
        holeSpacingMM: 4,
        sheetWidthMM: 300,
        spherized: useFreehand.getState().spherized,
        faceHoles: m.faceHoles || [],
        ...opts,
      });
    },
  };
}
